import React, { useMemo, useCallback, useRef } from 'react';
import { X, LogIn, LogOut } from 'lucide-react';
import { GoogleMap, useJsApiLoader, OverlayView } from '@react-google-maps/api';

interface TimeclockLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceName: string;
  clockIn: string | null;
  clockOut: string | null;
  clockInLat?: number | null;
  clockInLng?: number | null;
  clockOutLat?: number | null;
  clockOutLng?: number | null;
}

const containerStyle = { width: '100%', height: '100%' };

const libraries: ('places')[] = ['places'];

const mapOptions: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  scaleControl: false,
  rotateControl: false,
  clickableIcons: false,
  gestureHandling: 'greedy',
  styles: [
    { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  ],
};

function MapPin({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex flex-col items-center" style={{ transform: 'translate(-50%, -100%)' }}>
      <div
        className="flex items-center justify-center rounded-full shadow-lg border-2 border-white"
        style={{ width: 36, height: 36, backgroundColor: color }}
      >
        <span className="text-white font-bold" style={{ fontSize: label.length > 2 ? 9 : 12 }}>
          {label}
        </span>
      </div>
      <div
        style={{
          width: 0,
          height: 0,
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: `8px solid ${color}`,
          marginTop: -1,
        }}
      />
    </div>
  );
}

export default function TimeclockLocationModal({
  isOpen,
  onClose,
  resourceName,
  clockIn,
  clockOut,
  clockInLat,
  clockInLng,
  clockOutLat,
  clockOutLng,
}: TimeclockLocationModalProps) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapReady, setMapReady] = React.useState(false);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
    libraries,
  });

  const hasClockIn = clockInLat != null && clockInLng != null;
  const hasClockOut = clockOutLat != null && clockOutLng != null;

  const center = useMemo(() => {
    if (hasClockIn && hasClockOut) {
      return { lat: (clockInLat! + clockOutLat!) / 2, lng: (clockInLng! + clockOutLng!) / 2 };
    }
    if (hasClockIn) return { lat: clockInLat!, lng: clockInLng! };
    if (hasClockOut) return { lat: clockOutLat!, lng: clockOutLng! };
    return { lat: 49.2827, lng: -123.1207 };
  }, [clockInLat, clockInLng, clockOutLat, clockOutLng, hasClockIn, hasClockOut]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    if (hasClockIn && hasClockOut) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend({ lat: clockInLat!, lng: clockInLng! });
      bounds.extend({ lat: clockOutLat!, lng: clockOutLng! });
      map.fitBounds(bounds, 60);
    }
    setTimeout(() => setMapReady(true), 100);
  }, [clockInLat, clockInLng, clockOutLat, clockOutLng, hasClockIn, hasClockOut]);

  const pinsOverlap = hasClockIn && hasClockOut &&
    Math.abs(clockInLat! - clockOutLat!) < 0.0002 &&
    Math.abs(clockInLng! - clockOutLng!) < 0.0002;

  const zoom = useMemo(() => {
    if (hasClockIn && hasClockOut) return undefined;
    return 16;
  }, [hasClockIn, hasClockOut]);

  const formatTime = (iso: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '—';
    }
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '';
    }
  };

  React.useEffect(() => {
    if (!isOpen) setMapReady(false);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[100090] bg-black/50 transition-opacity duration-200"
        style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
        onClick={onClose}
      />

      <div
        className="fixed inset-y-0 right-0 z-[100090] w-full sm:w-[480px] bg-white shadow-2xl flex flex-col transform transition-transform duration-200 translate-x-0"
        style={{
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-[17px] font-semibold text-gray-900 truncate">Clock Location</h2>
            <p className="text-[13px] text-gray-500 truncate">{resourceName} · {formatDate(clockIn)}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 -mr-2 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors touch-manipulation"
            style={{ minWidth: 44, minHeight: 44 }}
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Map */}
        <div className="flex-1 min-h-0 bg-gray-100">
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={center}
              zoom={zoom}
              options={mapOptions}
              onLoad={onMapLoad}
            >
              {mapReady && hasClockIn && (
                <OverlayView
                  position={{
                    lat: clockInLat!,
                    lng: pinsOverlap ? clockInLng! - 0.00015 : clockInLng!,
                  }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <MapPin color="#22c55e" label="IN" />
                </OverlayView>
              )}
              {mapReady && hasClockOut && (
                <OverlayView
                  position={{
                    lat: clockOutLat!,
                    lng: pinsOverlap ? clockOutLng! + 0.00015 : clockOutLng!,
                  }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <MapPin color="#ef4444" label="OUT" />
                </OverlayView>
              )}
            </GoogleMap>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400 text-[15px]">
              Loading map…
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="flex-shrink-0 border-t border-gray-200 bg-white">
          <div className="px-4 py-4 space-y-3" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 1rem))' }}>
            {hasClockIn && (
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-9 h-9 rounded-full bg-green-50 border border-green-200">
                  <LogIn className="h-4 w-4 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-medium text-gray-900">Clock In — {formatTime(clockIn)}</p>
                  <p className="text-[12px] text-gray-400">
                    {clockInLat!.toFixed(6)}, {clockInLng!.toFixed(6)}
                  </p>
                </div>
              </div>
            )}
            {hasClockOut && (
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-9 h-9 rounded-full bg-red-50 border border-red-200">
                  <LogOut className="h-4 w-4 text-red-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-medium text-gray-900">Clock Out — {formatTime(clockOut)}</p>
                  <p className="text-[12px] text-gray-400">
                    {clockOutLat!.toFixed(6)}, {clockOutLng!.toFixed(6)}
                  </p>
                </div>
              </div>
            )}
            {!hasClockIn && !hasClockOut && (
              <p className="text-[15px] text-gray-400 text-center py-2">No location data available</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
